import React, { useRef, useState } from "react";
import AvatarEditor from "react-avatar-editor";
import { Button } from "antd";
import styled from "styled-components";
import { CUSTOM_COLORS } from "@/shared/theme/colorUtils";

const Container = styled.div`
	text-align: center;
	padding: 1rem 0 0 0;
`;

const EditorWrapper = styled.div`
	display: flex;
	flex-direction: column;
	align-items: center;
	gap: 1rem;
`;

const Controls = styled.div`
	display: flex;
	flex-direction: column;
	gap: 0.75rem;
	margin-top: 1.5rem;
`;

const PrimaryButton = styled(Button)`
	height: 40px;
	border-radius: 20px;
	font-size: 0.9rem;
	font-weight: 500;
	background: ${CUSTOM_COLORS.PRIMARY};
	border: none;
	width: 100%;
	color: #fff !important;

	&:hover {
		background: ${CUSTOM_COLORS.PRIMARY} !important;
		opacity: 0.9;
	}
`;

const SecondaryButton = styled(Button)`
	height: 40px;
	border-radius: 20px;
	font-size: 0.9rem;
	font-weight: 500;
	border: 1px solid #d9d9d9;
	background: white;
	color: #333;
	width: 100%;

	&:hover {
		border-color: ${CUSTOM_COLORS.PRIMARY};
		color: ${CUSTOM_COLORS.PRIMARY};
	}
`;

const UploadLabel = styled.label`
	display: inline-block;
	padding: 0.5rem 1rem;
	border: 1px dashed #d9d9d9;
	border-radius: 12px;
	cursor: pointer;
	color: #555;
`;

const Range = styled.input`
	width: 100%;
`;

interface UploadAvatarStepProps {
	onSkip: () => void;
	onAvatarSelected: (dataUrl: string) => void;
	loading?: boolean;
}

const UploadAvatarStep: React.FC<UploadAvatarStepProps> = ({ onSkip, onAvatarSelected, loading }) => {
	const editorRef = useRef<AvatarEditor | null>(null);
	const [image, setImage] = useState<File | null>(null);
	const [scale, setScale] = useState(1.2);

	const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
		const file = e.target.files?.[0] || null;
		setImage(file);
	};

	const handleNext = () => {
		if (editorRef.current) {
			const canvas = editorRef.current.getImageScaledToCanvas();
			const dataUrl = canvas.toDataURL("image/png");
			onAvatarSelected(dataUrl);
			return;
		}
		onSkip();
	};

	return (
		<Container>
			<EditorWrapper>
				{image ? (
					<AvatarEditor
						ref={editorRef}
						image={image}
						width={200}
						height={200}
						border={20}
						borderRadius={100}
						scale={scale}
					/>
				) : (
					<UploadLabel htmlFor="avatar-input">Choose an image</UploadLabel>
				)}
				<input
					id="avatar-input"
					type="file"
					accept="image/*"
					style={{ display: "none" }}
					onChange={handleFileChange}
				/>
				{image && (
					<Range
						type="range"
						min="1"
						max="3"
						step="0.1"
						value={scale}
						onChange={(e) => setScale(parseFloat(e.target.value))}
					/>
				)}
			</EditorWrapper>
			<Controls>
				<PrimaryButton onClick={handleNext} loading={loading} disabled={loading}>
					Next
				</PrimaryButton>
				<SecondaryButton onClick={onSkip} disabled={loading}>
					Skip for now
				</SecondaryButton>
			</Controls>
		</Container>
	);
};

export default UploadAvatarStep;
